import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import {
  DIRECTORY_PAGE_LIMIT_DEFAULT,
  OrganisationTypeSchema,
} from "@thalamos/haven-common";
import type {
  ConfigurationScopeSummary,
  CreateOrganisationRequest,
  Organisation,
  OrganisationType,
} from "@thalamos/haven-common";

import api, { apiHooks } from "../../api/index.js";
import { createRoute } from "../../routing/createRoute.js";
import type { AddOrganisationOutcome } from "./AddOrganisationDialog.js";
import { AddOrganisationDialog } from "./AddOrganisationDialog.js";
import { DirectoryFilter } from "./DirectoryFilter.js";
import { DirectoryTable } from "./DirectoryTable.js";
import type { ReadOutcome } from "./readOutcome.js";
import { useDirectoryQuery } from "./useDirectoryQuery.js";

const isOrganisationType = (value: string): value is OrganisationType =>
  OrganisationTypeSchema.safeParse(value).success;

/** Every organisation in Haven, filtered by name and kind. */
export const AdminOrganisationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { query, input, offset, setQuery, setOffset } = useDirectoryQuery();
  const [type, setType] = useState<OrganisationType>();
  const [adding, setAdding] = useState(false);

  const [{ data: scopes, loading: scopesLoading }] =
    apiHooks.configuration.scopes();
  const configurations: ReadOutcome<readonly ConfigurationScopeSummary[]> =
    scopes !== undefined
      ? { kind: "read", value: scopes.scopes }
      : scopesLoading
        ? { kind: "reading" }
        : { kind: "failed" };
  const [{ data, loading, error }] = apiHooks.admin.organisations({
    query,
    ...(type === undefined ? {} : { type: [type] }),
    offset,
    limit: DIRECTORY_PAGE_LIMIT_DEFAULT,
  });

  const columns = [
    {
      id: "name",
      labelKey: "admin.organisations.name",
      // The name is the link rather than the whole row: the row is the
      // organisation, so its name is what opens it.
      render: (organisation: Organisation) => (
        <Link
          component={RouterLink}
          to={createRoute.adminOrganisation(organisation.id)}
        >
          {organisation.name}
        </Link>
      ),
    },
    {
      id: "type",
      labelKey: "admin.organisations.type",
      render: (organisation: Organisation) =>
        t(`organisation.type.${organisation.type}`),
    },
  ];

  const add = async (
    organisation: CreateOrganisationRequest,
  ): Promise<AddOrganisationOutcome> => {
    const response = await api.admin.createOrganisation(organisation);

    if (response.status !== 201 || !("id" in response.data)) {
      return "failed";
    }

    navigate(createRoute.adminOrganisation(response.data.id));
    return "added";
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h5" component="h1">
          {t("admin.organisations.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setAdding(true);
          }}
        >
          {t("admin.organisations.add.open")}
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <DirectoryFilter
          value={input}
          label={t("admin.organisations.filter")}
          onChange={setQuery}
        />
        <TextField
          select
          size="small"
          label={t("admin.organisations.type")}
          value={type ?? ""}
          onChange={(event) => {
            const chosen = event.target.value;
            setType(isOrganisationType(chosen) ? chosen : undefined);
            setOffset(0);
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">{t("admin.organisations.allTypes")}</MenuItem>
          {OrganisationTypeSchema.options.map((each) => (
            <MenuItem key={each} value={each}>
              {t(`organisation.type.${each}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <DirectoryTable
        rows={data?.results ?? []}
        total={data?.total ?? 0}
        loading={loading}
        error={error !== undefined && error !== null}
        columns={columns}
        rowKey={(organisation) => organisation.id}
        limit={DIRECTORY_PAGE_LIMIT_DEFAULT}
        offset={offset}
        onPageChange={setOffset}
      />

      <AddOrganisationDialog
        open={adding}
        onClose={() => {
          setAdding(false);
        }}
        onAdd={add}
        configurations={configurations}
      />
    </Stack>
  );
};
